// wok - 24.9.26 - Web/Passkey: the browser side of the passkey logon (WebAuthn). Two pages use it:
//   Logon.aspx  - "Sign in with passkey" button and the conditional UI (passkey suggestions in the user name autofill):
//                 fetches the challenge from services/Passkey.ashx, lets the authenticator sign it and posts the assertion
//                 back to the page (hidden fields + __doPostBack), where Logon.LogonWithPasskey opens the session.
//   Start.aspx  - the passkey manager in the user menu: list, add (navigator.credentials.create), delete.
// Binary members travel as base64url strings (the server builds/reads them the same way, Code/Passkeys.vb).
namespace TdPasskeys {

   export interface Texts {
      loginButton: string;
      title: string;
      hint: string;
      add: string;
      delete: string;
      deleteConfirm: string;
      none: string;
      created: string;
      lastUsed: string;
      never: string;
      namePrompt: string;
      notSupported: string;
      failed: string;
      cancelled: string;
      registered: string;
      close: string;
      passwordLabel: string;
      passwordRequired: string;
      prfUnsupported: string;
   }

   export interface Config {
      serviceUrl: string;
      texts: Texts;
      // Start.aspx: the window of the page (the server checks it against the browser session)
      windowId?: string;
      // Logon.aspx: the postback target (UniqueID of the passkey button), its client id (busy spinner) and the ids of the fields on the page
      postBackTarget?: string;
      buttonId?: string;
      userNameFieldId?: string;
      connectionFieldId?: string;
      challengeFieldId?: string;
      responseFieldId?: string;
      messageId?: string;
   }

   var PANEL_ID: string = "td-passkeys";
   var mConfig: Config = null;
   var mConditionalAbort: AbortController = null;

   export function configure(pConfig: Config): void {
      mConfig = pConfig;
   }

   export function isSupported(): boolean {
      var lWindow: any = window;
      var lCredentials: any = navigator.credentials;
      return !!(lWindow.PublicKeyCredential && lCredentials && typeof lCredentials.get === "function" && typeof lCredentials.create === "function");
   }

   // ---------------------------------------------------------------- base64url

   function toBase64Url(pBuffer: ArrayBuffer): string {
      var lBytes = new Uint8Array(pBuffer);
      var lBinary = "";
      for (var i = 0; i < lBytes.length; i++) {
         lBinary += String.fromCharCode(lBytes[i]);
      }
      return window.btoa(lBinary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
   }

   function fromBase64Url(pText: string): Uint8Array {
      var lBase64 = pText.replace(/-/g, "+").replace(/_/g, "/");
      while (lBase64.length % 4 !== 0) {
         lBase64 += "=";
      }
      var lBinary = window.atob(lBase64);
      var lBytes = new Uint8Array(lBinary.length);
      for (var i = 0; i < lBinary.length; i++) {
         lBytes[i] = lBinary.charCodeAt(i);
      }
      return lBytes;
   }

   // ---------------------------------------------------------------- server calls

   function call(pAction: string, pBody: any, pDone: (pResult: any) => void, pFail: (pMessage: string) => void): void {
      var lUrl = mConfig.serviceUrl + "?action=" + encodeURIComponent(pAction);
      if (mConfig.windowId) {
         lUrl += "&window=" + encodeURIComponent(mConfig.windowId);
      }
      var lRequest = new XMLHttpRequest();
      lRequest.open("POST", lUrl, true);
      lRequest.setRequestHeader("Content-Type", "application/json; charset=utf-8");
      lRequest.onreadystatechange = function () {
         if (lRequest.readyState !== 4) { return; }
         var lJson: any = null;
         try {
            lJson = lRequest.responseText ? JSON.parse(lRequest.responseText) : {};
         } catch (e) {
            lJson = null;
         }
         if (lRequest.status >= 200 && lRequest.status < 300 && lJson) {
            pDone(lJson);
         } else {
            pFail((lJson && lJson.error) ? lJson.error : mConfig.texts.failed + " (" + lRequest.status + ")");
         }
      };
      lRequest.send(JSON.stringify(pBody || {}));
   }

   // the options JSON of the server -> the ArrayBuffer members navigator.credentials expects
   function toRequestOptions(pOptions: any): any {
      pOptions.challenge = fromBase64Url(pOptions.challenge);
      pOptions.allowCredentials = (pOptions.allowCredentials || []).map(function (pCredential: any) {
         return { type: pCredential.type, id: fromBase64Url(pCredential.id) };
      });
      convertPrfExtension(pOptions);
      return pOptions;
   }

   function toCreationOptions(pOptions: any): any {
      pOptions.challenge = fromBase64Url(pOptions.challenge);
      pOptions.user.id = fromBase64Url(pOptions.user.id);
      pOptions.excludeCredentials = (pOptions.excludeCredentials || []).map(function (pCredential: any) {
         return { type: pCredential.type, id: fromBase64Url(pCredential.id) };
      });
      convertPrfExtension(pOptions);
      return pOptions;
   }

   // wok - 27.9.26 - Web/Passkey: vault mode - the PRF extension (extensions.prf.eval.first = the site salt) makes the authenticator derive a
   // secret from the passkey; the server protects the Windows password with it and unlocks it at logon
   function convertPrfExtension(pOptions: any): void {
      var lPrf = pOptions.extensions && pOptions.extensions.prf;
      if (lPrf && lPrf.eval && typeof lPrf.eval.first === "string") {
         lPrf.eval.first = fromBase64Url(lPrf.eval.first);
      }
   }

   // the PRF secret the authenticator returned with a credential (create or get), base64url; null when there is none
   function prfSecretOf(pCredential: any): string {
      var lResults: any = (typeof pCredential.getClientExtensionResults === "function") ? pCredential.getClientExtensionResults() : null;
      var lPrf = lResults && lResults.prf;
      if (lPrf && lPrf.results && lPrf.results.first) {
         return toBase64Url(lPrf.results.first);
      }
      return null;
   }

   // some authenticators enable PRF at create but evaluate it only at get: a second, silent get with the new passkey delivers the secret
   function obtainPrfSecret(pCredential: any, pCreationOptions: any, pDone: (pSecret: string) => void): void {
      var lSecret = prfSecretOf(pCredential);
      if (lSecret) { pDone(lSecret); return; }
      var lResults: any = (typeof pCredential.getClientExtensionResults === "function") ? pCredential.getClientExtensionResults() : null;
      if (lResults && lResults.prf && lResults.prf.enabled === false) { pDone(null); return; }
      var lChallenge = new Uint8Array(32);
      window.crypto.getRandomValues(lChallenge);
      var lRequest: any = {
         publicKey: {
            challenge: lChallenge,
            rpId: pCreationOptions.rp.id,
            allowCredentials: [{ type: "public-key", id: pCredential.rawId }],
            userVerification: "preferred",
            extensions: pCreationOptions.extensions
         }
      };
      var lCredentials: any = navigator.credentials;
      lCredentials.get(lRequest).then(
         function (pAssertion: any) { pDone(prfSecretOf(pAssertion)); },
         function () { pDone(null); });
   }

   // the PublicKeyCredential of the browser -> the JSON the server verifies
   function credentialToJson(pCredential: any): any {
      var lResponse = pCredential.response;
      var lJson: any = {
         id: pCredential.id,
         rawId: toBase64Url(pCredential.rawId),
         type: pCredential.type,
         authenticatorAttachment: pCredential.authenticatorAttachment || null,
         response: { clientDataJSON: toBase64Url(lResponse.clientDataJSON) }
      };
      if (lResponse.attestationObject) {
         lJson.response.attestationObject = toBase64Url(lResponse.attestationObject);
         lJson.response.transports = (typeof lResponse.getTransports === "function") ? lResponse.getTransports() : [];
      }
      if (lResponse.authenticatorData) {
         lJson.response.authenticatorData = toBase64Url(lResponse.authenticatorData);
         lJson.response.signature = toBase64Url(lResponse.signature);
         lJson.response.userHandle = lResponse.userHandle ? toBase64Url(lResponse.userHandle) : null;
      }
      var lPrfSecret = prfSecretOf(pCredential);
      if (lPrfSecret) {
         lJson.clientExtensionResults = { prf: { first: lPrfSecret } };
      }
      return lJson;
   }

   function errorText(pError: any): string {
      if (pError && pError.name === "NotAllowedError") { return mConfig.texts.cancelled; }
      var lDetail = (pError && pError.message) ? " " + pError.message : "";
      return mConfig.texts.failed + lDetail;
   }

   // ---------------------------------------------------------------- Logon.aspx

   function fieldValue(pId: string): string {
      var lField = pId ? <HTMLInputElement>document.getElementById(pId) : null;
      return lField ? lField.value : "";
   }

   function setFieldValue(pId: string, pValue: string): void {
      var lField = <HTMLInputElement>document.getElementById(pId);
      if (lField) { lField.value = pValue; }
   }

   function showLogonMessage(pText: string, pIsError: boolean): void {
      var lMessage = mConfig.messageId ? document.getElementById(mConfig.messageId) : null;
      if (!lMessage) { return; }
      lMessage.innerHTML = "";
      lMessage.appendChild(document.createTextNode(pText));
      lMessage.style.color = pIsError ? "#d92d32" : "";
   }

   // the "Sign in with passkey" button: challenge -> authenticator -> postback with the signed assertion
   export function logon(): void {
      if (!mConfig) { return; }
      if (!isSupported()) {
         showLogonMessage(mConfig.texts.notSupported, true);
         return;
      }
      abortConditional();
      showLogonMessage("", false);
      lockLogonButtons(true);
      call("login-options", { userName: fieldValue(mConfig.userNameFieldId), connectionKey: fieldValue(mConfig.connectionFieldId) },
         function (pResult: any) {
            requestAssertion(pResult.challengeId, pResult.options, null);
         },
         function (pMessage: string) {
            showLogonMessage(pMessage, true);
            lockLogonButtons(false);
         });
   }

   // wok - 29.9.26 - Web: the logon buttons are locked with a spinner in the passkey button while the ceremony runs (LogonAspx.ts) and
   // released again when it fails or the user cancels the authenticator prompt
   function lockLogonButtons(pLocked: boolean): void {
      var lWindow: any = window;
      if (pLocked && typeof lWindow.LockLogonButtons === "function") { lWindow.LockLogonButtons(mConfig.buttonId); }
      if (!pLocked && typeof lWindow.UnlockLogonButtons === "function") { lWindow.UnlockLogonButtons(); }
   }

   function requestAssertion(pChallengeId: string, pOptions: any, pSignal: AbortSignal): void {
      var lRequest: any = { publicKey: toRequestOptions(pOptions) };
      if (pSignal) {
         lRequest.mediation = "conditional";
         lRequest.signal = pSignal;
      }
      var lCredentials: any = navigator.credentials;
      lCredentials.get(lRequest).then(
         function (pCredential: any) {
            mConditionalAbort = null;
            setFieldValue(mConfig.challengeFieldId, pChallengeId);
            setFieldValue(mConfig.responseFieldId, JSON.stringify(credentialToJson(pCredential)));
            var lWindow: any = window;
            if (typeof lWindow.ShowWaitIndicator === "function") { lWindow.ShowWaitIndicator(mConfig.buttonId); }
            lWindow.__doPostBack(mConfig.postBackTarget, "");
         },
         function (pError: any) {
            if (pError && pError.name === "AbortError") { return; } // the conditional request was cancelled by us
            showLogonMessage(errorText(pError), true);
            lockLogonButtons(false);
         });
   }

   // conditional UI: the browser offers the passkeys of this site in the autofill of the user name field
   // (input autocomplete="username webauthn"); picking one signs in without a click on the button
   export function startConditional(): void {
      if (!mConfig || !isSupported()) { return; }
      var lWindow: any = window;
      var lPublicKeyCredential: any = lWindow.PublicKeyCredential;
      if (typeof lPublicKeyCredential.isConditionalMediationAvailable !== "function" || typeof AbortController === "undefined") { return; }
      lPublicKeyCredential.isConditionalMediationAvailable().then(function (pAvailable: boolean) {
         if (!pAvailable) { return; }
         call("login-options", { userName: "", connectionKey: fieldValue(mConfig.connectionFieldId) },
            function (pResult: any) {
               mConditionalAbort = new AbortController();
               requestAssertion(pResult.challengeId, pResult.options, mConditionalAbort.signal);
            },
            function () { /* no conditional UI then */ });
      }, function () { /* not available */ });
   }

   function abortConditional(): void {
      if (mConditionalAbort) {
         try { mConditionalAbort.abort(); } catch (e) { }
         mConditionalAbort = null;
      }
   }

   // ---------------------------------------------------------------- Start.aspx: passkey manager

   function element(pTag: string, pClass: string, pText?: string): HTMLElement {
      var lElement = document.createElement(pTag);
      if (pClass) { lElement.className = pClass; }
      if (pText) { lElement.appendChild(document.createTextNode(pText)); }
      return lElement;
   }

   function panel(): HTMLElement {
      var lPanel = document.getElementById(PANEL_ID);
      if (lPanel) { return lPanel; }

      var lBackdrop = element("div", "td-passkeys-backdrop");
      lBackdrop.id = PANEL_ID + "-backdrop";
      lBackdrop.onclick = function () { closeManager(); };
      document.body.appendChild(lBackdrop);

      lPanel = element("div", "td-passkeys");
      lPanel.id = PANEL_ID;
      lPanel.setAttribute("role", "dialog");
      lPanel.setAttribute("aria-modal", "true");

      var lHeader = element("div", "td-passkeys-header");
      lHeader.appendChild(element("h3", "td-passkeys-title", mConfig.texts.title));
      var lClose = <HTMLButtonElement>element("button", "td-passkeys-close");
      lClose.type = "button";
      lClose.title = mConfig.texts.close;
      lClose.setAttribute("aria-label", mConfig.texts.close);
      lClose.innerHTML = "&times;";
      lClose.onclick = function () { closeManager(); };
      lHeader.appendChild(lClose);
      lPanel.appendChild(lHeader);

      lPanel.appendChild(element("p", "td-passkeys-hint", mConfig.texts.hint));

      // vault mode: the Windows password, asked once when the session has none (shown by add() on demand)
      var lPasswordRow = element("div", "td-passkeys-password");
      lPasswordRow.id = PANEL_ID + "-password-row";
      var lPasswordLabel = <HTMLLabelElement>element("label", "", mConfig.texts.passwordLabel);
      lPasswordLabel.htmlFor = PANEL_ID + "-password";
      lPasswordRow.appendChild(lPasswordLabel);
      var lPasswordInput = <HTMLInputElement>element("input", "");
      lPasswordInput.id = PANEL_ID + "-password";
      lPasswordInput.type = "password";
      lPasswordInput.autocomplete = "current-password";
      lPasswordRow.appendChild(lPasswordInput);
      lPanel.appendChild(lPasswordRow);

      var lList = element("ul", "td-passkeys-list");
      lList.id = PANEL_ID + "-list";
      lPanel.appendChild(lList);

      var lFooter = element("div", "td-passkeys-footer");
      var lAdd = <HTMLButtonElement>element("button", "td-passkeys-add", mConfig.texts.add);
      lAdd.type = "button";
      lAdd.onclick = function () { add(); };
      lFooter.appendChild(lAdd);
      var lStatus = element("div", "td-passkeys-status");
      lStatus.id = PANEL_ID + "-status";
      lFooter.appendChild(lStatus);
      lPanel.appendChild(lFooter);

      document.body.appendChild(lPanel);
      return lPanel;
   }

   function status(pText: string, pIsError: boolean): void {
      var lStatus = document.getElementById(PANEL_ID + "-status");
      if (!lStatus) { return; }
      lStatus.innerHTML = "";
      lStatus.appendChild(document.createTextNode(pText || ""));
      lStatus.className = "td-passkeys-status" + (pIsError ? " td-passkeys-status-error" : "");
   }

   function formatDate(pIso: string): string {
      if (!pIso) { return mConfig.texts.never; }
      var lDate = new Date(pIso);
      return isNaN(lDate.getTime()) ? pIso : lDate.toLocaleString();
   }

   function render(pResult: any): void {
      var lList = document.getElementById(PANEL_ID + "-list");
      if (!lList) { return; }
      lList.innerHTML = "";
      var lPasskeys: any[] = (pResult && pResult.passkeys) ? pResult.passkeys : [];
      if (lPasskeys.length === 0) {
         lList.appendChild(element("li", "td-passkeys-empty", mConfig.texts.none));
         return;
      }
      for (var i = 0; i < lPasskeys.length; i++) {
         var lPasskey = lPasskeys[i];
         var lItem = element("li", "td-passkeys-item");
         var lText = element("div", "td-passkeys-item-text");
         lText.appendChild(element("div", "td-passkeys-item-name", lPasskey.name || ""));
         lText.appendChild(element("div", "td-passkeys-item-meta", mConfig.texts.created + ": " + formatDate(lPasskey.createdUtc) + " · " + mConfig.texts.lastUsed + ": " + formatDate(lPasskey.lastUsedUtc)));
         lItem.appendChild(lText);
         var lDelete = <HTMLButtonElement>element("button", "td-passkeys-delete", mConfig.texts.delete);
         lDelete.type = "button";
         lDelete.onclick = deleteHandler(lPasskey.id);
         lItem.appendChild(lDelete);
         lList.appendChild(lItem);
      }
   }

   function deleteHandler(pId: string): (pEvent: MouseEvent) => void {
      return function () { remove(pId); };
   }

   function refresh(): void {
      call("list", {}, render, function (pMessage: string) { status(pMessage, true); });
   }

   // a proposal for the passkey name from the device the user is on
   function defaultName(): string {
      var lAgent = navigator.userAgent || "";
      if (/iPhone/i.test(lAgent)) { return "iPhone"; }
      if (/iPad/i.test(lAgent)) { return "iPad"; }
      if (/Android/i.test(lAgent)) { return "Android"; }
      if (/Macintosh/i.test(lAgent)) { return "Mac"; }
      if (/Windows/i.test(lAgent)) { return "Windows"; }
      if (/Linux/i.test(lAgent)) { return "Linux"; }
      return "Passkey";
   }

   // Start.aspx: the configuration is inline (var tdPasskeyConfig) and appears after this script was loaded, so it is read on first use
   function ensureConfig(): boolean {
      if (!mConfig) {
         var lInlineConfig: any = (<any>window).tdPasskeyConfig;
         if (lInlineConfig) { configure(lInlineConfig); }
      }
      return !!mConfig;
   }

   export function openManager(): void {
      if (!ensureConfig()) { return; }
      var lPanel = panel();
      lPanel.style.display = "block";
      var lBackdrop = document.getElementById(PANEL_ID + "-backdrop");
      if (lBackdrop) { lBackdrop.style.display = "block"; }
      status(isSupported() ? "" : mConfig.texts.notSupported, !isSupported());
      refresh();
   }

   export function closeManager(): void {
      var lPanel = document.getElementById(PANEL_ID);
      if (lPanel) { lPanel.style.display = "none"; }
      var lBackdrop = document.getElementById(PANEL_ID + "-backdrop");
      if (lBackdrop) { lBackdrop.style.display = "none"; }
   }

   function passwordField(): HTMLInputElement {
      return <HTMLInputElement>document.getElementById(PANEL_ID + "-password");
   }

   export function add(): void {
      if (!isSupported()) {
         status(mConfig.texts.notSupported, true);
         return;
      }
      status("", false);
      call("register-options", {},
         function (pResult: any) {
            // vault mode without a password in the session: ask for it, the next click on "Add" sends it along
            var lPasswordRow = document.getElementById(PANEL_ID + "-password-row");
            var lPassword = passwordField() ? passwordField().value : "";
            if (pResult.passwordRequired && !lPassword) {
               if (lPasswordRow) { lPasswordRow.style.display = "block"; }
               if (passwordField()) { passwordField().focus(); }
               status(mConfig.texts.passwordRequired, true);
               return;
            }
            var lName = window.prompt(mConfig.texts.namePrompt, defaultName());
            if (lName === null) { return; }
            var lOptions = toCreationOptions(pResult.options);
            var lCredentials: any = navigator.credentials;
            lCredentials.create({ publicKey: lOptions }).then(
               function (pCredential: any) {
                  var lJson = credentialToJson(pCredential);
                  var lRegister = function () {
                     var lBody: any = { challengeId: pResult.challengeId, name: lName, credential: lJson };
                     if (pResult.passwordRequired) { lBody.password = lPassword; }
                     call("register", lBody,
                        function (pList: any) {
                           if (passwordField()) { passwordField().value = ""; }
                           if (lPasswordRow) { lPasswordRow.style.display = "none"; }
                           render(pList);
                           status(mConfig.texts.registered, false);
                        },
                        function (pMessage: string) { status(pMessage, true); });
                  };
                  if (pResult.vault) {
                     obtainPrfSecret(pCredential, lOptions, function (pSecret: string) {
                        if (!pSecret) {
                           status(mConfig.texts.prfUnsupported, true);
                           return;
                        }
                        lJson.clientExtensionResults = { prf: { first: pSecret } };
                        lRegister();
                     });
                  } else {
                     lRegister();
                  }
               },
               function (pError: any) {
                  status(errorText(pError), true);
               });
         },
         function (pMessage: string) { status(pMessage, true); });
   }

   export function remove(pId: string): void {
      if (!window.confirm(mConfig.texts.deleteConfirm)) { return; }
      call("delete", { id: pId }, render, function (pMessage: string) { status(pMessage, true); });
   }
}
